import { FC } from "react";
import { Avatar, Button, Checkbox, Stack, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import StyledLink from "@components/StyledLink/StyledLink";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { Patient } from "@models/patient";
import { DateUtil } from "@utils/date.util";
import {
  getPatientDisplayName,
  getPatientStatusKey,
} from "@utils/patient.util";

export interface PatientRowProps {
  patient: Patient;
  selected: boolean;
  onSelect: (selected: boolean) => void;
  onEdit: () => void;
  onToggleActive: () => void;
  onArchiveToggle: () => void;
  onOpenPortal: () => void;
  onEditProfile: () => void;
  isMutating?: boolean;
}

const PatientRow: FC<PatientRowProps> = ({
  patient,
  selected,
  onSelect,
  onEdit,
  onToggleActive,
  onArchiveToggle,
  onOpenPortal,
  onEditProfile,
  isMutating = false,
}) => {
  const { t } = useTranslation();
  const status = getPatientStatusKey(patient);
  const name = getPatientDisplayName(patient);

  const stopPropagation = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <TableRow hover sx={{ cursor: "pointer" }} onClick={onEdit}>
      <TableCell padding="checkbox" onClick={stopPropagation}>
        <Checkbox
          checked={selected}
          onChange={(_, checked) => onSelect(checked)}
        />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Avatar
            sx={{ width: 36, height: 36, flexShrink: 0 }}
            alt={name}
            src={patient.avatarUrl}
          >
            <PersonOutlineIcon fontSize="small" />
          </Avatar>
          <StyledLink to="" onClick={onEdit}>
            {name}
          </StyledLink>
        </Stack>
      </TableCell>
      <TableCell>{t(`patientManage.status.${status}`)}</TableCell>
      <TableCell>
        {patient.lastActivityAt
          ? DateUtil.formatDateTime(patient.lastActivityAt)
          : t("patientManage.never")}
      </TableCell>
      <TableCell align="right" onClick={stopPropagation}>
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          justifyContent="flex-end"
          flexWrap="wrap"
        >
          <Tooltip title={t("patientManage.editProfile")}>
            <Button
              size="small"
              variant="text"
              aria-label={t("patientManage.editProfile")}
              onClick={onEditProfile}
              sx={{ minWidth: 36, p: 0.5 }}
            >
              <ModeEditOutlineOutlinedIcon fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip
            title={
              patient.isActive
                ? t("patientManage.openPortal")
                : t("patientManage.portalInactive")
            }
          >
            <span>
              <Button
                size="small"
                variant="text"
                aria-label={t("patientManage.openPortal")}
                disabled={!patient.isActive || isMutating}
                onClick={onOpenPortal}
                sx={{ minWidth: 36, p: 0.5 }}
              >
                <HomeOutlinedIcon fontSize="small" />
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            disabled={isMutating}
            onClick={onToggleActive}
          >
            {patient.isActive
              ? t("patientManage.deactivate")
              : t("patientManage.activate")}
          </Button>
          <Button
            size="small"
            disabled={isMutating}
            onClick={onArchiveToggle}
          >
            {patient.archivedAt
              ? t("patientManage.unarchive")
              : t("patientManage.archive")}
          </Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default PatientRow;
